// The code map tab: the folder's C# files as cards, members as rows, calls as lines. Test files
// are hidden at first; rows count the tests that reach their member.
import { testedBy, type CodeMap } from '@modelviewer/core';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Eye, FlaskConical, FlaskConicalOff, Link, Loader2, RefreshCw, Repeat, Shuffle } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { Canvas, Divider, MENU_LABEL, SEGMENT, ToolButton, type CanvasSelection, type CanvasTarget, type Vocabulary } from '../diagram/Canvas';
import { useCardFonts } from '../diagram/DiagramView';
import { measureText } from '../diagram/measure';
import { layoutInput, useLayout } from '../diagram/useLayout';
import { showSidebar } from '../state/sidebars';
import { loadCodeMap, mappedFiles, selectCode, useCodeMap } from '../state/codemap';
import { useWorkspace } from '../state/workspace';
import { codeIndex } from './codeIndex';
import { buildCodeGraph, codeCounts, testFilesOf } from './graph';
import { useCodeSettings, type CodeToggle, type TestFiles } from './settings';

const MODES: Vocabulary['modes'] = [
  { value: 'all', label: 'All', title: 'Every member of every type' },
  { value: 'keys', label: 'Linked', title: 'Only members with a line' },
  { value: 'none', label: 'Types', title: 'Only the types in each file' },
];

const TOGGLES: { key: CodeToggle; on: string; off: string; icon: React.ReactNode }[] = [
  { key: 'insideFile', on: 'Hide calls inside a file', off: 'Show calls inside a file', icon: <Repeat size={15} /> },
  { key: 'guesses', on: 'Hide guessed calls (dashed)', off: 'Show guessed calls', icon: <Shuffle size={15} /> },
  { key: 'uses', on: 'Hide files that use each other’s types', off: 'Show files that use each other’s types', icon: <Link size={15} /> },
  { key: 'access', on: 'Hide reads and writes of properties and fields', off: 'Show reads and writes of properties and fields', icon: <Eye size={15} /> },
];

const TEST_FILES: { value: TestFiles; label: string; title: string }[] = [
  { value: 'hide', label: 'Hide', title: 'Leave test files out of the map' },
  { value: 'dim', label: 'Dim', title: 'Draw test files faintly' },
  { value: 'show', label: 'Show', title: 'Draw test files like the rest' },
];

function TestFilesMenu({ count }: { count: number }) {
  const tests = useCodeSettings((s) => s.tests);
  const setTests = useCodeSettings((s) => s.setTests);
  const label = `Test files: ${tests === 'hide' ? 'hidden' : tests === 'dim' ? 'dimmed' : 'shown'}`;
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger asChild>
        <button
          type="button"
          title={label}
          aria-label={label}
          className="flex h-7 items-center gap-0.5 rounded-md px-1.5 text-muted hover:bg-hover hover:text-text data-[state=open]:bg-active data-[state=open]:text-text pointer-coarse:h-9"
        >
          {tests === 'hide' ? <FlaskConicalOff size={15} /> : <FlaskConical size={15} />}
          <ChevronDown size={12} className="text-faint" />
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="start" sideOffset={6} collisionPadding={8} className="mv-menu z-50 w-[252px] rounded-lg border border-border-strong bg-panel-2 p-1.5 shadow-[var(--shadow-pop)]">
          <Menu.Label className={MENU_LABEL}>Test files</Menu.Label>
          <Menu.RadioGroup value={tests} onValueChange={(v) => setTests(v as TestFiles)} className="grid grid-cols-3 gap-1">
            {TEST_FILES.map((t) => (
              <Menu.RadioItem key={t.value} value={t.value} title={t.title} className={SEGMENT}>
                {t.label}
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
          <p className="px-1.5 pt-2 pb-1 text-[11.5px] leading-snug text-faint">
            {count} test {count === 1 ? 'file' : 'files'}. Each member row shows how many tests reach it.
          </p>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** How many tests reach each member (skipped tests left out). */
function testCountsOf(map: CodeMap, guesses: boolean): Map<string, number> {
  const skipped = new Set(map.tests.filter((t) => t.skipped).map((t) => t.id));
  const counts = new Map<string, number>();
  for (const [member, tests] of testedBy(map, { guesses })) {
    const n = tests.filter((t) => !skipped.has(t.test)).length;
    if (n) counts.set(member, n);
  }
  return counts;
}

export function CodeMapView() {
  const map = useCodeMap((s) => s.map);
  const loading = useCodeMap((s) => s.loading);
  const error = useCodeMap((s) => s.error);
  const source = useWorkspace((s) => s.source);
  const settings = useCodeSettings();
  const fontsReady = useCardFonts();

  useEffect(() => {
    if (!useCodeMap.getState().map && !useCodeMap.getState().loading) void loadCodeMap();
  }, [source]);

  const testCounts = useMemo(() => (map?.tests.length ? testCountsOf(map, settings.guesses) : undefined), [map, settings.guesses]);
  const testFiles = useMemo(() => (map ? testFilesOf(map).size : 0), [map]);
  const graph = useMemo(
    () =>
      map && fontsReady
        ? buildCodeGraph(map, {
            members: settings.members,
            insideFile: settings.insideFile,
            access: settings.access,
            guesses: settings.guesses,
            uses: settings.uses,
            tests: settings.tests,
            testCounts,
            measure: measureText,
          })
        : null,
    [map, fontsReady, settings.members, settings.insideFile, settings.access, settings.guesses, settings.uses, settings.tests, testCounts],
  );
  const input = useMemo(() => (graph ? layoutInput(graph) : null), [graph]);
  // With test files the map has other cards: their own arrangement (dimmed and shown share one).
  const layoutKey = source ? `${source.id}|code|${settings.members}${settings.tests === 'hide' ? '' : '|tests'}` : null;
  const { positions, save, relayout, pending } = useLayout(layoutKey, input);

  const selection = useCodeMap((s) => s.selection);
  const selected = useMemo<CanvasSelection>(() => {
    if (!selection || !map) return { node: null, row: null, edge: null };
    if (selection.kind === 'file') return { node: selection.path, row: null, edge: null };
    if (selection.kind === 'edge') return { node: null, row: null, edge: selection.id };
    const file = codeIndex(map).fileOf(selection.id);
    return { node: null, row: file ? `${file}::${selection.id}` : null, edge: null };
  }, [selection, map]);

  const onSelect = useCallback((target: CanvasTarget | null) => {
    if (!target) selectCode(null);
    else if (target.kind === 'node') selectCode({ kind: 'file', path: target.node.key });
    else if (target.kind === 'row') selectCode({ kind: 'member', id: target.row.id });
    else selectCode({ kind: 'edge', id: target.edge.id });
  }, []);

  const request = useCodeMap((s) => s.reveal);
  const reveal = useMemo(() => (request?.view === 'code' ? { id: request.path, nonce: request.nonce, focus: request.focus } : null), [request]);

  const vocabulary = useMemo<Vocabulary>(
    () => ({ search: 'Find file, type or member', modesLabel: 'Members to show', modes: MODES, mode: settings.members, setMode: settings.setMembers, joins: false }),
    [settings.members, settings.setMembers],
  );

  if (error) return <div className="flex h-full items-center justify-center p-6 text-center text-muted">{error}</div>;
  if (map && map.files.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center text-muted">
        <p>No C# files to map in this folder.</p>
        <p className="text-[12px] text-faint">Generated files (migrations, snapshots, *.Designer.cs) and build output are left out.</p>
      </div>
    );
  }
  if (!graph || !positions) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted" data-testid="codemap-loading">
        <Loader2 size={16} className="animate-spin" />
        {loading ? `Reading code… ${loading.read} of ${loading.total} files` : 'Laying out the code map…'}
      </div>
    );
  }

  const counts = codeCounts(map!, settings.tests);
  const generated = mappedFiles().length - map!.stats.files;
  return (
    <Canvas
      graph={graph}
      positions={positions}
      selected={selected}
      onSelect={onSelect}
      onMoved={save}
      onRelayout={() => void relayout()}
      fitKey={`codemap|${source?.id}`}
      reveal={reveal}
      vocabulary={vocabulary}
      tools={
        <>
          <Divider />
          {TOGGLES.map((t) => (
            <ToolButton key={t.key} label={settings[t.key] ? t.on : t.off} active={settings[t.key]} onClick={() => settings.toggle(t.key)}>
              {t.icon}
            </ToolButton>
          ))}
          {testFiles > 0 && <TestFilesMenu count={testFiles} />}
          <Divider />
          <ToolButton label="Read the code again" onClick={() => void loadCodeMap()}>
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          </ToolButton>
        </>
      }
      status={
        <button
          type="button"
          className="text-left"
          onClick={() => {
            selectCode(null);
            showSidebar('inspector');
          }}
          title="How the code map reads the code"
        >
          {counts.files} files{generated > 0 ? ` (${generated} generated left out)` : ''}
          {counts.hiddenTests > 0 && ` · ${counts.hiddenTests} test ${counts.hiddenTests === 1 ? 'file' : 'files'} hidden`} · {counts.calls} calls
          {counts.guessed > 0 && <> ({counts.guessed} guessed)</>}
          {pending && <span data-testid="codemap-arranging"> · arranging the cards…</span>}
        </button>
      }
    />
  );
}
